import { useCallback } from 'react';

import clsx from 'clsx';

import { useDropZone } from './hooks/useDropZone';

interface FileDropZoneProps {
  onDrop: (files: File[]) => void;
  className?: string;
  activeClassName?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  acceptedFormats?: string[];
  allowMultipleFiles?: boolean;
  onClick?: () => void;
}

export const FileDropZone = ({
  onDrop,
  className = '',
  activeClassName = '',
  children,
  disabled = false,
  acceptedFormats,
  allowMultipleFiles = true,
  onClick
}: FileDropZoneProps) => {
  const { isDragging, dragHandlers } = useDropZone({ disabled });

  const getValidFiles = (files: File[], acceptedFormats?: string[]): File[] => {
    if (!acceptedFormats || acceptedFormats.length === 0) {
      return files;
    }

    return files.filter((file) =>
      acceptedFormats.some((format) => {
        if (format.endsWith('/*')) {
          const type = format.split('/')[0];
          return file.type.startsWith(type + '/');
        }
        return file.type === format;
      })
    );
  };

  const handleFileDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) {
        return;
      }

      const files = Array.from(e.dataTransfer.files);

      if (files.length === 0) {
        return;
      }

      const validFiles = getValidFiles(files, acceptedFormats);

      if (validFiles.length === 0) {
        return;
      }

      const filesToDrop = allowMultipleFiles ? validFiles : [validFiles[0]];
      onDrop(filesToDrop);
    },
    [disabled, acceptedFormats, allowMultipleFiles, onDrop]
  );

  return (
    <div
      className={clsx(className, isDragging && activeClassName)}
      onDrop={handleFileDrop}
      onClick={disabled ? undefined : onClick}
      {...(onClick && {
        role: 'button',
        tabIndex: disabled ? -1 : 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }
      })}
      {...dragHandlers}>
      {children}
    </div>
  );
};
